import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageShell, { SectionTitle } from './PageShell';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  Plus, Calendar, Clock, Users, ArrowRight, X, Loader2, Play, CheckCircle2,
  Presentation, FileDown, Eye, Sparkles, BookOpen,
} from 'lucide-react';

export default function TeacherSeancesPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [seances, setSeances] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [students, setStudents] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [creating, setCreating] = useState(false);
  const [pickedTpl, setPickedTpl] = useState(null);
  const [pickedStudent, setPickedStudent] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== 'enseignant') { navigate('/'); return; }
    Promise.all([
      api.teacherSeances(),
      api.seanceTemplates(),
      api.teacherStudents(),
    ]).then(([s, t, st]) => {
      setSeances(s); setTemplates(t); setStudents(st.students || []);
    });
  }, [loading, user, navigate]);

  const create = async () => {
    if (!pickedTpl || !pickedStudent) return;
    setCreating(true);
    try {
      const seance = await api.createSeance({ student_id: pickedStudent, template_id: pickedTpl });
      navigate(`/enseignant/seance/${seance.id}`);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Erreur');
    } finally { setCreating(false); }
  };

  if (loading) return <PageShell><div className="text-white/60 mt-16 px-6"><Loader2 className="animate-spin" /></div></PageShell>;

  return (
    <PageShell>
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <SectionTitle
            kicker="Cours en salle · RDV pédagogique"
            title="Mes séances pédagogiques"
            sub="Organisez vos cours en salle (alcool, vitesse, distance, etc.) et vos RDV pédagogiques AAC avec génération automatique du compte-rendu."
          />
          <button data-testid="seances-new-btn" onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13.5px] font-semibold shadow-lg shadow-red-900/40 self-start">
            <Plus size={15} /> Nouvelle séance
          </button>
        </div>

        {/* Bibliothèque des 8 cours — toujours visible, même sans élève lié */}
        <div className="mb-10 rounded-2xl border border-white/10 bg-gradient-to-br from-[#0f0a0a] to-[#0d0d0e] p-6 lg:p-7">
          <div className="flex items-center gap-3 mb-1">
            <Sparkles className="text-red-400" size={16} />
            <span className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold">Bibliothèque · accès libre</span>
          </div>
          <h2 className="font-serif-editorial text-white text-[24px] leading-tight mb-1">Parcourir les {templates.length} cours interactifs</h2>
          <p className="text-white/55 text-[13px] mb-5 max-w-2xl">Mode <strong className="text-white">aperçu</strong> — consultez ou projetez n'importe quel cours en salle <strong>sans avoir besoin d'élève lié</strong>. Idéal pour préparer une séance, présenter aux parents, ou animer un cours collectif. Le compte-rendu PDF nécessite ensuite la création d'une séance liée à un élève.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {templates.map((t) => (
              <Link
                key={t.id}
                to={`/enseignant/apercu/${t.id}`}
                data-testid={`tpl-preview-${t.id}`}
                className="group rounded-xl border border-white/10 bg-white/[0.03] p-4 hover:border-red-700/50 hover:bg-red-950/10 transition block"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[9.5px] uppercase tracking-wider text-red-400 font-bold truncate">{t.category}</span>
                  <span className="text-[10px] text-white/40 font-mono-spec shrink-0 ml-2">{t.duration_min}m</span>
                </div>
                <div className="font-serif-editorial text-white text-[14px] leading-tight mb-3 group-hover:text-red-300 transition line-clamp-2">{t.title}</div>
                <div className="inline-flex items-center gap-1.5 text-white/55 text-[11.5px] group-hover:text-red-300 transition">
                  <Eye size={11} /> Aperçu · {t.steps?.length || 0} étapes
                  <ArrowRight size={10} className="group-hover:translate-x-0.5 transition" />
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Liste séances créées */}
        <h2 className="font-serif-editorial text-white text-[22px] mb-4 flex items-center gap-2">
          <BookOpen size={16} className="text-red-400" /> Mes séances officielles (liées à un élève)
        </h2>
        {seances.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-10 text-center text-white/60">
            <Presentation className="mx-auto mb-3 text-white/30" size={32} />
            <p className="text-[14px]">Aucune séance officielle pour l'instant.</p>
            <p className="text-white/45 text-[12px] mt-2 max-w-md mx-auto">
              Les séances officielles permettent de générer un compte-rendu PDF signé. Elles nécessitent un élève lié à votre compte (via votre code enseignant).
            </p>
            <button onClick={() => setShowNew(true)} className="mt-4 inline-flex items-center gap-2 h-10 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-semibold">
              <Plus size={14} /> Créer ma première séance officielle
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {seances.map((s) => (
              <Link key={s.id} to={`/enseignant/seance/${s.id}`} data-testid={`seance-card-${s.id}`} className="group rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 hover:border-red-700/40 transition block">
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-md ${s.status === 'done' ? 'bg-green-950/40 text-green-300 border border-green-800/40' : s.status === 'in_progress' ? 'bg-orange-950/40 text-orange-300 border border-orange-800/40' : 'bg-white/[0.06] text-white/70 border border-white/15'}`}>
                    {s.status === 'done' ? '✓ Terminée' : s.status === 'in_progress' ? '◐ En cours' : '○ À démarrer'}
                  </span>
                  <span className="text-[11px] text-white/40 font-mono-spec">{s.duration_min}min</span>
                </div>
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-red-400 font-bold mb-1">{s.category}</div>
                <h3 className="font-serif-editorial text-white text-[18px] leading-tight mb-3 group-hover:text-red-300 transition">{s.title}</h3>
                <div className="flex items-center gap-2 text-white/55 text-[12px]"><Users size={12} /> {s.student_name}</div>
                <div className="mt-4 inline-flex items-center gap-1.5 text-red-400 text-[12.5px] font-semibold">
                  {s.status === 'done' ? <><FileDown size={13} /> Voir le compte-rendu</> : <><Play size={13} /> Ouvrir la séance</>}
                  <ArrowRight size={12} className="group-hover:translate-x-0.5 transition" />
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Modal nouvelle séance */}
        {showNew && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end md:items-center justify-center p-4" onClick={() => setShowNew(false)}>
            <div className="bg-[#0d0d0e] border border-white/15 rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 lg:p-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold mb-1">Étape 1 / 2</div>
                  <h3 className="font-serif-editorial text-white text-[24px]">Quel modèle de séance ?</h3>
                </div>
                <button data-testid="seances-modal-close" onClick={() => setShowNew(false)} className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"><X size={14} /></button>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 mb-6">
                {templates.map((t) => (
                  <button
                    key={t.id}
                    data-testid={`tpl-${t.id}`}
                    onClick={() => setPickedTpl(t.id)}
                    className={`text-left p-4 rounded-xl border transition ${pickedTpl === t.id ? 'border-red-500 bg-red-950/25' : 'border-white/10 bg-white/[0.03] hover:border-white/20'}`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] uppercase tracking-wider text-red-400 font-bold">{t.category}</span>
                      <span className="text-[10px] text-white/40 font-mono-spec">{t.duration_min}min</span>
                    </div>
                    <div className="font-serif-editorial text-white text-[16px] leading-tight mb-1.5">{t.title}</div>
                    <p className="text-white/55 text-[11.5px] leading-relaxed line-clamp-2">{t.description}</p>
                    <div className="mt-2 text-[10px] text-white/40 font-mono-spec">Audience · {t.audience.replace('_', ' & ')}</div>
                  </button>
                ))}
              </div>

              {pickedTpl && (
                <>
                  <div className="border-t border-white/10 pt-5">
                    <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold mb-1">Étape 2 / 2</div>
                    <h3 className="font-serif-editorial text-white text-[24px] mb-4">Pour quel élève ?</h3>
                  </div>
                  {students.length === 0 ? (
                    <p className="text-white/55 text-[13.5px] p-4 rounded-xl border border-yellow-900/30 bg-yellow-950/15 text-yellow-200/85">
                      Vous n'avez aucun élève lié. Communiquez votre code enseignant à un élève pour qu'il vous lie depuis son tableau de bord.
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                      {students.map((s) => (
                        <button
                          key={s.id}
                          data-testid={`pick-student-${s.id}`}
                          onClick={() => setPickedStudent(s.id)}
                          className={`w-full flex items-center justify-between p-3 rounded-xl border text-left transition ${pickedStudent === s.id ? 'border-red-500 bg-red-950/25' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'}`}
                        >
                          <div>
                            <div className="text-white text-[13.5px] font-semibold">{s.name}</div>
                            <div className="text-white/50 text-[12px]">{s.email} · {s.livret_done}/38 ({s.livret_pct}%)</div>
                          </div>
                          {pickedStudent === s.id && <CheckCircle2 className="text-red-400" size={16} />}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="mt-6 flex justify-end gap-3 border-t border-white/10 pt-5">
                    <button onClick={() => setShowNew(false)} className="h-11 px-5 rounded-full bg-white/[0.04] border border-white/10 text-white/75 text-[13px] hover:bg-white/[0.08]">Annuler</button>
                    <button data-testid="seances-create-confirm" onClick={create} disabled={!pickedStudent || creating} className="h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13.5px] font-semibold inline-flex items-center gap-2 disabled:opacity-50">
                      {creating ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Démarrer la séance
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </section>
    </PageShell>
  );
}
